import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FirebaseContext } from "../contexts/FirebaseContext";
import { useCommonUI } from "../contexts/CommonUIContext";
import ActionItem from "../components/ActionItem";
import { moveItem, computeReindexUpdates } from "../utils/actionsOrdering";
import { isAiInvoiceEnabled } from "../utils/billingRateSetting";

const TaskActionsContainer = ({ task }) => {
  const {
    fetchActions,
    createAction,
    updateAction,
    deleteAction,
    convertActionToTask,
    restoreAction,
    deleteTask,
    setTaskTrackingState,
    setTaskAiTrackingState,
    finalizeActionTimers,
  } = useContext(FirebaseContext);
  const { showSnackbar } = useCommonUI();

  const [newActionLabel, setNewActionLabel] = useState("");

  // `actions` is the displayed (and locally reorderable) list. A ref mirrors
  // it so the drag-end handler always persists the latest order.
  const [actions, setActions] = useState([]);
  const actionsRef = useRef([]);
  useEffect(() => {
    actionsRef.current = actions;
  }, [actions]);

  // After the user adds an action, scroll the list to the bottom so the new
  // row is visible (iOS parity). The add echoes back through the subscription
  // asynchronously, so we set a flag here and scroll once `actions` re-renders.
  const containerRef = useRef(null);
  const pendingScrollRef = useRef(false);

  const scrollNewActionIntoView = useCallback(() => {
    // Walk up to the nearest scrollable ancestor (the tab's scroll area) and
    // jump to the bottom. The list reserves 84px below it, clearing the fixed
    // add-action footer, so the newest row lands above the input.
    let el = containerRef.current?.parentElement;
    while (el) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if (
        (overflowY === "auto" || overflowY === "scroll") &&
        el.scrollHeight > el.clientHeight
      ) {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        return;
      }
      el = el.parentElement;
    }
  }, []);

  useEffect(() => {
    if (pendingScrollRef.current) {
      pendingScrollRef.current = false;
      scrollNewActionIntoView();
    }
  }, [actions, scrollNewActionIntoView]);

  // Subscribe to this task's actions in real time; resubscribe only when the
  // task changes, and clean up on unmount. The listener already returns them
  // ordered by actionOrder, so the incoming order is authoritative.
  useEffect(() => {
    if (task?.id) {
      const unsubscribe = fetchActions(task.id, setActions);
      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [task?.id, fetchActions]);

  // Optimistic local reorder while dragging (no writes yet).
  const moveAction = useCallback((fromIndex, toIndex) => {
    setActions((prev) => moveItem(prev, fromIndex, toIndex));
  }, []);

  // On drop, persist the new order: reindex actionOrder 0..N-1 and write only
  // the rows whose position changed (each write bumps updatedAt so Android's
  // last-write-wins sync picks it up). Mirrors the Android reorder behavior.
  const persistOrder = useCallback(() => {
    computeReindexUpdates(actionsRef.current).forEach(({ id, actionOrder }) =>
      updateAction(id, { actionOrder })
    );
  }, [updateAction]);

  // Reindex the remaining actions 0..N-1 after a removal, writing only rows
  // whose position changed. Mirrors Android's MarkActionAsDelete reorder.
  const reindexExcluding = useCallback(
    (removedId) => {
      const remaining = actionsRef.current.filter((a) => a.id !== removedId);
      setActions(remaining); // optimistic: drop the row immediately
      computeReindexUpdates(remaining).forEach(({ id, actionOrder }) =>
        updateAction(id, { actionOrder })
      );
    },
    [updateAction]
  );

  const handleDelete = useCallback(
    async (action) => {
      await deleteAction(action.id);
      reindexExcluding(action.id);
    },
    [deleteAction, reindexExcluding]
  );

  const handleAddAction = useCallback(async () => {
    const label = newActionLabel.trim();
    if (!label || !task?.id) return;
    setNewActionLabel(""); // clear immediately, like the Android input
    pendingScrollRef.current = true; // scroll to the new row once it renders
    await createAction(task.id, label, actionsRef.current);
  }, [newActionLabel, task?.id, createAction]);

  const handleConvertToTask = useCallback(
    async (action) => {
      const newTaskId = await convertActionToTask(action, task);
      if (!newTaskId) return; // empty label → nothing converted, keep the row
      await deleteAction(action.id);
      reindexExcluding(action.id);

      // Undo: tombstone the created task and restore the original action.
      showSnackbar({
        message: "Action converted to task",
        actionLabel: "Undo",
        onAction: async () => {
          await deleteTask(newTaskId);
          await restoreAction(action);
        },
      });
    },
    [
      convertActionToTask,
      deleteAction,
      reindexExcluding,
      task,
      showSnackbar,
      deleteTask,
      restoreAction,
    ]
  );

  // AI action button shows only when AI Invoice is on AND this task's "Track AI"
  // toggle is on (mirrors Android's gating).
  const showAiTracking =
    isAiInvoiceEnabled() && task?.aiTrackingEnabled !== false;
  // An action is "tracked" when the task's tracked-action id points at it.
  const sameId = (a, b) => a != null && Number(a) === Number(b);

  return (
    // Bottom padding clears the fixed add-action footer below.
    <div ref={containerRef} style={{ paddingBottom: "84px" }}>
      <h2>Task Actions</h2>
      {actions.length > 0 ? (
        <DndProvider backend={HTML5Backend}>
          <ul style={styles.list}>
            {actions.map((action, index) => {
              const isTracked =
                !!task?.taskBeingTracked &&
                sameId(task?.trackedActionId, action.id);
              const isAiTracked =
                !!task?.aiBeingTracked &&
                sameId(task?.aiTrackedActionId, action.id);
              return (
                <li key={action.id} style={styles.listItem}>
                  <ActionItem
                    action={action}
                    index={index}
                    moveAction={moveAction}
                    onDropEnd={persistOrder}
                    onConvertToTask={handleConvertToTask}
                    onDelete={handleDelete}
                    showAiTracking={showAiTracking}
                    isTracked={isTracked}
                    isAiTracked={isAiTracked}
                    onTrackToggle={() =>
                      setTaskTrackingState(
                        task.id,
                        task.projectId,
                        !isTracked,
                        action.id
                      )
                    }
                    onAiTrackToggle={() =>
                      setTaskAiTrackingState(
                        task.id,
                        task.projectId,
                        !isAiTracked,
                        action.id
                      )
                    }
                    onCheckFinalize={() =>
                      finalizeActionTimers(task.id, task.projectId, action.id)
                    }
                  />
                </li>
              );
            })}
          </ul>
        </DndProvider>
      ) : (
        <p>No actions found for this task.</p>
      )}

      {/* Pinned above the bottom bar so the action list scrolls behind it and
          the input is always reachable (matches the Records tab footer). */}
      <div style={styles.footer}>
        <div style={styles.addRow}>
          <input
            type="text"
            value={newActionLabel}
            onChange={(e) => setNewActionLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddAction();
            }}
            placeholder="Enter action content"
            style={styles.addInput}
          />
          <button
            onClick={handleAddAction}
            disabled={!newActionLabel.trim()}
            style={{
              ...styles.addButton,
              ...(newActionLabel.trim() ? {} : styles.addButtonDisabled),
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  list: {
    listStyleType: "none",
    padding: 0,
    margin: 0,
  },
  listItem: {
    marginBottom: "8px",
  },
  // Fixed footer pinned just above the 60px bottom nav bar.
  footer: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: "60px",
    padding: "12px 16px",
    background: "var(--bg)",
    borderTop: "1px solid var(--border)",
    boxSizing: "border-box",
    zIndex: 900,
  },
  addRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  addInput: {
    flex: 1,
    padding: "12px",
    fontSize: "16px",
    border: "1px solid var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--text)",
    borderRadius: "8px",
    outline: "none",
  },
  addButton: {
    backgroundColor: "var(--primary)",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    flexShrink: 0,
  },
  addButtonDisabled: {
    backgroundColor: "var(--border)",
    color: "var(--text-faint)",
    cursor: "not-allowed",
  },
};

export default TaskActionsContainer;
